import React, { useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useSelector, useDispatch } from 'react-redux'

import * as selectors from './audio.selectors'
import * as actions from './audio.actions'
import * as savePointSelectors from '../../SavePoint/savePoint.selectors'

import AudioPlayer from '../../AudioPlayer'

/**
 *  Container for audios. Manage all audios presentation.
 */
const AudiosContainer = () => {
  const dispatch = useDispatch()

  // Redux selectors usando hooks
  const audios = useSelector(selectors.getAudios)
  const backgroundAudios = useSelector(selectors.getBackgroundAudios)
  const savePoint = useSelector(savePointSelectors.getSavePoint)

  // Action creators
  const pauseAudio = useCallback((id) => dispatch(actions.pauseAudio(id)), [dispatch])
  const resumeAudio = useCallback((id) => dispatch(actions.resumeAudio(id)), [dispatch])

  // useEffect para manejar cambios en backgroundAudios
  // Este efecto se ejecuta cuando backgroundAudios cambia
  useEffect(() => {
    if (backgroundAudios.length > 1) {
      let maxPriority = Math.max.apply(
        Math,
        backgroundAudios.map((audio) => audio.priority || 0)
      )
      let audioMax = backgroundAudios.find((audio) => audio.priority === maxPriority)
      backgroundAudios.forEach((audio) => {
        if (audio.id !== audioMax.id && !audio.paused) {
          pauseAudio(audio.id)
        }
      })
    } else {
      let audio = backgroundAudios[0]
      if (audio && audio.paused) {
        resumeAudio(audio.id)
      }
    }
  }, [backgroundAudios, pauseAudio, resumeAudio])

  /**
   *  Render each audio available
   */
  const renderAudio = useCallback(
    (audio) => {
      const activeEffects = savePoint.settings.soundEffects
      const activeBackground = savePoint.settings.soundBackground
      const activeVoices = savePoint.settings.soundVoices

      if (!audio) {
        throw new Error('You are trying to play an audio that does not exists')
      }
      if (audio.paused) {
        return null
      }
      const audioProps = {
        ...audio,
        key: audio.id,
      }

      if (!audioProps.url) {
        return null
      }

      if (
        (!activeEffects && audio.type === 'effect') ||
        (!activeBackground && audio.type === 'background') ||
        (!activeVoices && audio.type === 'voices')
      ) {
        return null
      }

      return <AudioPlayer {...audioProps} />
    },
    [savePoint.settings]
  )

  /**
   *  Render container
   */
  if (audios.length === 0) {
    return null
  }

  return <span style={{ visibility: 'hidden' }}>{audios.map((audio) => renderAudio(audio))}</span>
}

// PropTypes no necesarios con hooks
// AudiosContainer.propTypes = {}

export default AudiosContainer
